import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CityChoiceResult, RealSitePreview } from "@/shared/contracts";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Button, EmptyState, ErrorState, Icon, LoadingState, Page, TopBar } from "@/shared/ui";
import { CityPicker, cityBlocked, useCityStatus, type CityPick } from "@/features/identity/CityPicker";
import { realSitePreviewKey } from "./RealSiteNotice";
import "./realSite.css";

/** 后端 changes／keeps 的代码 → 玩家看的说法（RealSitePreview 字段说明：outing_start、nearby_places；city、habitat、memories、friends、birth_record） */
const CHANGE_TEXT: Record<string, string> = { outing_start: "出门的起点", nearby_places: "附近能去的地方" };
const KEEP_TEXT: Record<string, string> = { city: "城市", habitat: "生活环境", memories: "回忆", friends: "朋友", birth_record: "出生记录" };

/** 不能换的原因码 → 一句真话（只说这一次接口给的那一个原因，不列成可勾选的清单；I 10-09 对 C5 的意见） */
export function realSiteReasonText(reason: string | null | undefined): string {
  switch (reason) {
    case "already_real": return "TA 已经住在城里一个真实的小区了，不用再换。";
    case "city_not_chosen": return "这个家还没选过城市。先选一座城，TA 的家会落在那座城里一个真实的小区。";
    case "region_unsupported": return "这个地区暂时还没开放。";
    case "city_not_ready":
    case "no_site_in_area": return "这一片的住处还在准备，准备好了就能换。";  // 不许「过一会儿」：开城什么时候好说不准（6c2b 10-13）
    case "not_admin": return "家的住处由家庭管理员来定，请管理员来给 TA 找住处。";
    case "pet_away": return "等 TA 回家再换。";
    default: return "现在还不能换住处。";
  }
}

/** 确认、选城被拒时的说法：按后端 details.reason；结果不明（断网、服务端出错）单独说，不当成被拒 */
function writeProblem(error: unknown, idempotent: boolean): string {
  const problem = toApiError(error);
  const reason = typeof problem.details?.reason === "string" ? problem.details.reason : null;
  if (problem.kind !== "http" || problem.status === null || problem.status >= 500) {
    return idempotent ? "结果还没确认。同一座城再点一次不会重复记。" : "结果还没确认，先别重复点，过一会儿回来看看。";
  }
  if (reason === "preview_changed") return "住处有变化，已经换成最新的，请再看一下。";
  if (reason === "habitat_not_supported") return "这座城没有 TA 现在的生活环境，换一座城看看。";
  if (reason) return realSiteReasonText(reason);
  return problem.playerMessage;
}

/**
 * 老家补真实住处的预览与确认（UI2-HOME-SMALL-01；参考稿 ASSET-UI-030 C5，DOM 实现）。
 * - 读 GET /home/place/real-site/preview：能换就给新住处（小区名只给本家人看）、离原来多远（后端给了才写）、会变的和不变的；
 *   不能换就照原因码说一句真话；从没选过城的先在这页选城（复用入住页的 CityPicker，POST /home/place/city），选完重新预览。
 * - 「就住这里」带预览给的 site_id 确认；成功后让 queryKeys.homePlace(petId)（住处、告知、预览）和家园快照失效，入住页、出发站读到的是新住处。
 * - 按钮用主色令牌，不照参考图取深绿；参考图上的「已有 12 位伙伴」之类示意数字不写。演示模式不换住处。
 */
export function RealSitePage() {
  const [params] = useSearchParams();
  const household = useOptionalCurrentHousehold();
  const petId = params.get("pet")?.trim() || household?.pet?.pet_id || null;
  if (env.dataMode !== "live") {
    return (
      <Page bare className="ps-real-site-page">
        <TopBar title="住处" back="/home" backLabel="回小窝" />
        <EmptyState icon="home" title="演示里不换住处">真实的住处只在正式的家里才能换。</EmptyState>
      </Page>
    );
  }
  if (!petId) {
    return (
      <Page bare className="ps-real-site-page">
        <TopBar title="住处" back="/home" backLabel="回小窝" />
        <EmptyState icon="home" title="先回小窝">从小窝进来，就知道是给哪只伙伴找住处。</EmptyState>
      </Page>
    );
  }
  // 当前账号能管的宠物里没有这一只（旧链接、刚换了家庭、宠物被移出）：不拿它去拉预览，中性地说一句（6c2b 10-13）；
  // 名单还在读时也先不拉。全站 ?pet= 链接参数由家庭上下文先处理，这里是本页自己的一道。
  if (household && !household.pets.length) return <Page bare className="ps-real-site-page"><TopBar title="住处" back="/home" backLabel="回小窝" /><LoadingState lines={2} label="正在确认是哪只伙伴…" /></Page>;
  if (household && !household.pets.some((p) => p.pet.pet_id === petId)) {
    return (
      <Page bare className="ps-real-site-page">
        <TopBar title="住处" back="/home" backLabel="回小窝" />
        <EmptyState icon="home" title="找不到这只宠物的住处信息" action={<Link className="ps-btn ps-btn--secondary ps-btn--sm" to="/home">回小窝</Link>} />
      </Page>
    );
  }
  return <RealSiteFor petId={petId} />;
}

function RealSiteFor({ petId }: { petId: string }) {
  const { homePlace } = useServices();
  const client = useQueryClient();
  const preview = useQuery({ queryKey: realSitePreviewKey(petId), queryFn: ({ signal }) => homePlace.realSitePreview(petId, signal), retry: false, refetchOnWindowFocus: false });
  const [done, setDone] = useState<RealSitePreview | null>(null);
  const [cityDone, setCityDone] = useState<CityChoiceResult | null>(null);
  const [pick, setPick] = useState<CityPick | null>(null);
  const cityStatus = useCityStatus(pick?.cityId);
  const refreshPlace = () => {
    void client.invalidateQueries({ queryKey: queryKeys.homePlace(petId) });
    void client.invalidateQueries({ queryKey: queryKeys.home });
  };
  const confirm = useMutation({
    mutationFn: (siteId: string) => homePlace.confirmRealSite(siteId, petId),
    onSuccess: (placed) => { setDone(placed); refreshPlace(); },
    onError: (error) => {
      // 预览之后落点变了、或者结果不明：重读一次预览，看最新的样子（已经落下的话会变成 already_real）
      const reason = toApiError(error).details?.reason;
      if (reason === "preview_changed" || toApiError(error).kind !== "http") void preview.refetch();
    },
  });
  const chooseCity = useMutation({
    mutationFn: (cityId: string) => homePlace.chooseCity(cityId, petId),
    onSuccess: (chosen) => { setCityDone(chosen); setPick(null); refreshPlace(); },
  });

  const back = <TopBar title="住处" back="/home" backLabel="回小窝" />;
  if (done) {
    return (
      <Page bare className="ps-real-site-page">
        {back}
        <section className="ps-real-site__done" role="status" data-testid="real-site-done">
          <Icon name="home" size={28} />
          <h2>住好了</h2>
          <p>{done.private_display ? `TA 的家在 ${done.private_display}，出门就从那里出发。` : "TA 的家落在了城里一个真实的小区，出门就从那里出发。"}</p>
          <Link className="ps-btn ps-btn--primary ps-btn--block" to="/home">回小窝</Link>
        </section>
      </Page>
    );
  }
  return (
    <Page bare className="ps-real-site-page">
      {back}
      <header className="ps-real-site__head">
        <h2>给 TA 找一个真实的住处</h2>
        {/* 只有能换时才说「先看看、确认后再落定」；不能换时这句和下面「暂时还不能换」打架（6c2b 10-13） */}
        {preview.data?.can_change ? <p>先看看新住处，确认后再落定。</p> : null}
      </header>
      {cityDone ? (
        <p className="ps-real-site__note" role="status" data-testid="real-site-city-done">
          {cityDone.place.address_pending
            ? `城市定好了：${cityDone.place.city}。新家地址还在落定。落定之前，TA 先待在家里，不出门。${cityDone.opening_available ? "" : "这座城还在准备，落定的时间现在说不准。"}`
            : `城市定好了：${cityDone.place.city}。`}
        </p>
      ) : null}
      {preview.isPending ? <LoadingState lines={2} label="正在看新住处…" /> : preview.isError ? (
        <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
      ) : (
        // 刚选了城、城还在准备：上面那个照实的框已经说了，不再出第二个「暂时还不能换」（6c2b 10-13）
        <PreviewBody preview={preview.data} hideBlocked={Boolean(cityDone) && (preview.data.reason === "city_not_ready" || preview.data.reason === "no_site_in_area")} />
      )}
      {preview.data && !preview.data.can_change && preview.data.reason === "city_not_chosen" && !cityDone ? (
        <section className="ps-real-site__city" aria-labelledby="real-site-city-title">
          <h2 id="real-site-city-title">挑一座城</h2>
          <CityPicker petId={petId} pick={pick} onPick={setPick} status={cityStatus} />
          {chooseCity.isError ? <p className="ps-real-site__problem" role="status" data-testid="real-site-city-problem">{writeProblem(chooseCity.error, true)}</p> : null}
          <Button variant="primary" block disabled={!pick || cityBlocked(cityStatus.data, cityStatus.error) || chooseCity.isPending} loading={chooseCity.isPending}
            onClick={() => { if (pick) chooseCity.mutate(pick.cityId); }}>
            {pick ? `选${pick.name}` : "选一座城"}
          </Button>
        </section>
      ) : null}
      {confirm.isError ? <p className="ps-real-site__problem" role="status" data-testid="real-site-problem">{writeProblem(confirm.error, false)}</p> : null}
      <div className="ps-real-site__actions">
        {/* 有得选（能换）时是「先不了」；不能换、只能回去时就叫「回小窝」 */}
        <Link className="ps-btn ps-btn--secondary" to="/home">{preview.data?.can_change && preview.data.site_id ? "先不了" : "回小窝"}</Link>
        {preview.data?.can_change && preview.data.site_id ? (
          <Button variant="primary" disabled={confirm.isPending} loading={confirm.isPending} onClick={() => confirm.mutate(preview.data!.site_id!)}>
            就住这里
          </Button>
        ) : null}
      </div>
    </Page>
  );
}

function PreviewBody({ preview, hideBlocked = false }: { preview: RealSitePreview; hideBlocked?: boolean }) {
  const changes = preview.changes.map((code) => CHANGE_TEXT[code]).filter(Boolean);
  const keeps = preview.keeps.map((code) => KEEP_TEXT[code]).filter(Boolean);
  return (
    <>
      {preview.can_change && preview.private_display ? (
        <section className="ps-real-site__site" aria-label="新住处" data-testid="real-site-site">
          <span className="ps-real-site__kicker">新住处</span>
          <strong>{preview.private_display}</strong>
          {typeof preview.distance_km === "number" ? <span>离原来的住处约 {preview.distance_km} 公里</span> : null}
        </section>
      ) : null}
      {!preview.can_change && !hideBlocked ? (
        <section className="ps-real-site__blocked" aria-labelledby="real-site-blocked-title" data-testid="real-site-blocked">
          <h2 id="real-site-blocked-title"><Icon name="info" size={18} />{preview.reason === "already_real" ? "已经是真实的住处" : preview.reason === "city_not_chosen" ? "先选一座城" : "暂时还不能换"}</h2>
          <p>{realSiteReasonText(preview.reason)}</p>
          {preview.reason === "already_real" && preview.private_display ? <p>现在住在 {preview.private_display}。</p> : null}
        </section>
      ) : null}
      {preview.can_change && (changes.length || keeps.length) ? (
        <div className="ps-real-site__diff">
          {changes.length ? (
            <section aria-labelledby="real-site-changes"><h2 id="real-site-changes"><Icon name="sprout" size={18} />会变的</h2><ul>{changes.map((t) => <li key={t}>{t}</li>)}</ul></section>
          ) : null}
          {keeps.length ? (
            <section aria-labelledby="real-site-keeps"><h2 id="real-site-keeps"><Icon name="heart" size={18} />不变的</h2><ul>{keeps.map((t) => <li key={t}>{t}</li>)}</ul></section>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
